import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VisualStackContainer, { AUTO_MS } from './VisualStack';

function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = window.innerWidth;
    return w <= 767 ? 'mobile' : w <= 1023 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setBp(w <= 767 ? 'mobile' : w <= 1023 ? 'tablet' : 'desktop');
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return bp;
}

export default function HomeParallax({ projects, onViewProject }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const stackRef    = useRef(null);
  const containerRef = useRef(null);
  const bp = useBreakpoint();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); stackRef.current?.scroll(e.deltaY); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let startX = 0;
    const onTouchStart = (e) => { startX = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
      const deltaX = e.changedTouches[0].clientX - startX;
      if (Math.abs(deltaX) > 50) stackRef.current?.scroll(deltaX < 0 ? 100 : -100);
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  const project = projects[activeIdx] ?? projects[0];
  if (!project) return null;

  return (
    <div
      ref={containerRef}
      className="hp-container"
      style={{ width: '100%', height: '100vh', display: 'flex', overflow: 'hidden', backgroundColor: 'transparent' }}
    >
      <div className="hp-visual-stack" style={{ flex: bp === 'tablet' ? '0 0 54%' : '0 0 60%', position: 'relative', overflow: 'hidden' }}>
        <VisualStackContainer
          ref={stackRef}
          items={projects.map(p => ({ id: p.id, image: p.images[0]?.src, mobileImage: p.mobileHeroImage || null, title: p.title, category: p.tags[0], type: p.type }))}
          onActiveChange={setActiveIdx}
          onOpenModal={(id) => onViewProject(projects.find(p => p.id === id))}
        />
      </div>

      <TextPanel
        project={project}
        activeIdx={activeIdx}
        total={projects.length}
        onViewProject={onViewProject}
        goTo={(idx) => stackRef.current?.goTo(idx)}
        bp={bp}
      />
    </div>
  );
}

function TextPanel({ project, activeIdx, total, onViewProject, goTo, bp }) {
  const isDetailPage = project.type === 'uiux';
  const panelFlex    = bp === 'tablet' ? '0 0 42%' : '0 0 30%';
  const panelPadding = bp === 'tablet' ? '0 2rem 0 2.5rem' : '0 3rem 0 60px';
  return (
    <div
      className="hp-text-panel"
      style={{ flex: panelFlex, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: panelPadding, position: 'relative' }}
    >
      {/* dot indicator */}
      <div className="hp-indicator">
        {Array.from({ length: total }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to project ${i + 1}`}
            style={{
              height: '5px',
              width:  i === activeIdx ? '18px' : '5px',
              backgroundColor: i === activeIdx ? '#1E5BFF' : 'rgba(0,0,0,0.15)',
              border: 'none', cursor: 'pointer', padding: 0,
              transition: 'width 350ms ease, background-color 350ms ease',
            }}
          />
        ))}
      </div>

      {/* content block */}
      <div key={activeIdx} className="hp-content hp-left-in">
        <div className="hp-meta-row">
          {project.year && <span className="hp-meta-year">{project.year}</span>}
          {project.role && <span className="hp-meta-role">{project.role}</span>}
          <span className="hp-meta-cat">{project.tags[0]}</span>
        </div>

        <h2 className="hp-title">{project.title}</h2>
        <p className="hp-desc">{project.shortDescription}</p>

        {!project.isMainOnly && (
          <div className="hp-ctas">
            {isDetailPage ? (
              <Link to={`/work/${project.id}`} className="hp-btn-view">
                <span className="hp-btn-view-label">
                  View Project
                  <svg className="hp-btn-view-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="hp-btn-view-underline" aria-hidden="true" />
              </Link>
            ) : (
              <button type="button" onClick={() => onViewProject(project)} className="hp-btn-view">
                <span className="hp-btn-view-label">
                  View Project
                  <svg className="hp-btn-view-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="hp-btn-view-underline" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* progress bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', backgroundColor: 'rgba(30,91,255,0.08)' }}>
        <div
          key={`pb-${activeIdx}`}
          style={{
            height: '100%', backgroundColor: '#1E5BFF',
            transformOrigin: 'left',
            animation: `hpProgressFill ${AUTO_MS}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
}
